import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { runScheduler, runRecipientScheduler, purgeScheduleItems, fetchAuthUsers, impersonateUser, testEmailConnection, sendTestEmail } from "api";
import { PageHeader, Modal, Select } from "components/common";
import { useAppDispatch } from "hooks/useAppStore";
import { startImpersonation } from "store/slices/authSlice";

type ToolResult = { created: number; skipped: number; errors: unknown[] };
type Week = "this_week" | "next_week";

export default function ToolsPage() {
  const navigate     = useNavigate();
  const dispatch     = useAppDispatch();
  const queryClient  = useQueryClient();
  const [schedulerWeek, setSchedulerWeek]       = useState<Week>("this_week");
  const [recipientWeek, setRecipientWeek]       = useState<Week>("this_week");
  const [schedulerResult, setSchedulerResult]   = useState<ToolResult | null>(null);
  const [recipientResult, setRecipientResult]   = useState<ToolResult | null>(null);

  const [purgeStart, setPurgeStart]             = useState("");
  const [purgeEnd, setPurgeEnd]                 = useState("");
  const [purgeConfirmed, setPurgeConfirmed]     = useState(false);
  const [purgeResult, setPurgeResult]           = useState<{ deleted: number } | null>(null);

  const [testEmailTo, setTestEmailTo]           = useState("");
  const [smtpResult, setSmtpResult]             = useState<{ success: boolean; domain?: string; message: string } | null>(null);
  const [sendEmailResult, setSendEmailResult]   = useState<{ success: boolean; message: string } | null>(null);

  const scheduler = useMutation({
    mutationFn: (week: Week) => runScheduler(week === "next_week" ? 1 : 0),
    onSuccess:  (data) => setSchedulerResult(data),
  });

  const recipient = useMutation({
    mutationFn: (week: Week) => runRecipientScheduler(week === "next_week" ? 1 : 0),
    onSuccess:  (data) => setRecipientResult(data),
  });

  const purge = useMutation({
    mutationFn: () => purgeScheduleItems({ start_date: purgeStart, end_date: purgeEnd }),
    onSuccess:  (data) => { setPurgeResult(data); setPurgeConfirmed(false); },
  });

  const smtpTest = useMutation({
    mutationFn: testEmailConnection,
    onSuccess:  (data) => setSmtpResult(data),
  });

  const emailSend = useMutation({
    mutationFn: () => sendTestEmail(testEmailTo),
    onSuccess:  (data) => setSendEmailResult(data),
  });

  // ─── Assume User ────────────────────────────────────────────────────────────
  const [selectedAuthUserId, setSelectedAuthUserId] = useState<number | "">("");

  const { data: authUsers } = useQuery({
    queryKey: ["auth_users"],
    queryFn:  fetchAuthUsers,
  });

  const assume = useMutation({
    mutationFn: (id: number) => impersonateUser(id),
    onSuccess:  (data) => {
      dispatch(startImpersonation(data));
      queryClient.clear();
      navigate("/");
    },
  });

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8">
      <PageHeader title="Tools" subtitle="Admin utilities" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Run scheduler tile */}
{/*        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Run scheduler</h2>
          <p className="text-xs text-gray-500 mb-4">
            Generates pickup items based on active donator schedules.
          </p>

          <WeekToggle value={schedulerWeek} onChange={setSchedulerWeek} />

          <button
            className="btn-primary w-full mt-3"
            onClick={() => { setSchedulerResult(null); scheduler.mutate(schedulerWeek); }}
            disabled={scheduler.isPending}
          >
            {scheduler.isPending ? "Running…" : "Run now"}
          </button>

          {schedulerResult && <ResultCard result={schedulerResult} label="Scheduler complete" />}
        </div>*/}

        {/* Process recipient schedule tile */}
{/*        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Process recipient schedule</h2>
          <p className="text-xs text-gray-500 mb-4">
            Creates drop-off items for distributor partners from active donator pickups.
          </p>

          <WeekToggle value={recipientWeek} onChange={setRecipientWeek} />

          <button
            className="btn-primary w-full mt-3"
            onClick={() => { setRecipientResult(null); recipient.mutate(recipientWeek); }}
            disabled={recipient.isPending}
          >
            {recipient.isPending ? "Running…" : "Run now"}
          </button>

          {recipientResult && <ResultCard result={recipientResult} label="Recipient scheduler complete" />}
        </div>*/}

        {/* Import bulk partners tile */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Import bulk partners</h2>
          <p className="text-xs text-gray-500 mb-4">
            Upload a CSV to create multiple Partner Entities, addresses, and contacts in one step.
          </p>
          <button
            className="btn-secondary w-full mt-3"
            onClick={() => navigate("/tools/import-partners")}
          >
            Upload CSV
          </button>
        </div>

        {/* Mailer tools tile */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Mailer tools</h2>
          <p className="text-xs text-gray-500 mb-4">
            Trigger notification-layer cron jobs, open the GoodJob dashboard, and review email delivery history.
          </p>
          <button
            className="btn-secondary w-full mt-3"
            onClick={() => navigate("/tools/mailer-tools")}
          >
            Open mailer tools
          </button>
        </div>

        {/* Courier availability calendar tile */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Courier availability calendar</h2>
          <p className="text-xs text-gray-500 mb-4">
            View weekly availability schedules for all active couriers, color-coded by courier.
          </p>
          <button
            className="btn-secondary w-full mt-3"
            onClick={() => navigate("/tools/courier-calendar")}
          >
            Open calendar
          </button>
        </div>

        {/* Test email connection tile */}
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Test email connection</h2>
          <p className="text-xs text-gray-500 mb-4">
            Verify SMTP configuration and send a test message to a given address.
          </p>

          {/* SMTP ping */}
          <p className="text-xs font-medium text-gray-700 mb-1">SMTP connection</p>
          <button
            className="btn-secondary w-full"
            onClick={() => { setSmtpResult(null); smtpTest.mutate(); }}
            disabled={smtpTest.isPending}
          >
            {smtpTest.isPending ? "Connecting…" : "Test connection"}
          </button>

          {smtpResult && (
            <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${smtpResult.success ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`}>
              <p className="font-medium">{smtpResult.success ? "Connected" : "Failed"}</p>
              {smtpResult.domain && (
                <p className="text-gray-600 mt-0.5">Domain: <strong>{smtpResult.domain}</strong></p>
              )}
              <p className="mt-0.5">{smtpResult.message}</p>
            </div>
          )}

          {smtpTest.isError && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
              {(smtpTest.error as Error)?.message ?? "An error occurred"}
            </div>
          )}

          {/* Send test email */}
          <p className="text-xs font-medium text-gray-700 mt-4 mb-1">Send test email</p>
          <input
            type="email"
            placeholder="recipient@example.com"
            value={testEmailTo}
            onChange={(e) => { setTestEmailTo(e.target.value); setSendEmailResult(null); }}
            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs mb-2"
          />
          <button
            className="btn-primary w-full"
            onClick={() => { setSendEmailResult(null); emailSend.mutate(); }}
            disabled={emailSend.isPending || !testEmailTo.trim()}
          >
            {emailSend.isPending ? "Sending…" : "Send test email"}
          </button>

          {sendEmailResult && (
            <div className={`mt-3 rounded-lg px-3 py-2 text-xs ${sendEmailResult.success ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`}>
              <p className="font-medium">{sendEmailResult.success ? "Sent" : "Failed"}</p>
              <p className="mt-0.5">{sendEmailResult.message}</p>
            </div>
          )}

          {emailSend.isError && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
              {(emailSend.error as Error)?.message ?? "An error occurred"}
            </div>
          )}
        </div>

        {/* Assume User tile — dev/stage only */}
        <div className="card p-6 border-2 border-amber-200">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-sm font-semibold text-gray-800">Assume user</h2>
            <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
              DEV TOOL
            </span>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Assume the persona of any auth user for this session. Resets on page refresh.
          </p>

          <div className="mb-3">
            <label className="block text-xs text-gray-500 mb-1">Select user</label>
            <Select
              size="sm"
              className="w-full"
              value={String(selectedAuthUserId)}
              onChange={(e) => setSelectedAuthUserId(e.target.value === "" ? "" : Number(e.target.value))}
            >
              <option value="">— choose a user —</option>
              {authUsers?.map((u) => (
                <option key={u.id} value={u.id} disabled={!u.active}>
                  {u.name ?? u.email} ({u.role}){!u.active ? " [inactive]" : ""}
                </option>
              ))}
            </Select>
          </div>

          <button
            className="w-full rounded-lg border border-amber-400 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800 hover:bg-amber-100 transition-colors disabled:opacity-40"
            disabled={selectedAuthUserId === "" || assume.isPending}
            onClick={() => selectedAuthUserId !== "" && assume.mutate(selectedAuthUserId)}
          >
            {assume.isPending ? "Assuming…" : "Assume persona"}
          </button>

          {assume.isError && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
              {(assume.error as Error)?.message ?? "An error occurred"}
            </div>
          )}
        </div>

        {/* Purge schedule items tile — dev/stage only */}
        <div className="card p-6 border-2 border-red-200">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="text-sm font-semibold text-gray-800">Purge schedule items</h2>
            <span className="text-xs font-semibold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
              NOT FOR PRODUCTION
            </span>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Permanently removes (soft-deletes) all schedule items within a date range.
            This action cannot be undone and is blocked in production.
          </p>

          <div className="space-y-2 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Start date</label>
              <input
                type="date"
                value={purgeStart}
                onChange={(e) => { setPurgeStart(e.target.value); setPurgeConfirmed(false); setPurgeResult(null); }}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">End date</label>
              <input
                type="date"
                value={purgeEnd}
                onChange={(e) => { setPurgeEnd(e.target.value); setPurgeConfirmed(false); setPurgeResult(null); }}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs"
              />
            </div>
          </div>

          {!purgeConfirmed ? (
            <button
              className="w-full mt-1 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-100 transition-colors disabled:opacity-40"
              disabled={!purgeStart || !purgeEnd}
              onClick={() => setPurgeConfirmed(true)}
            >
              Review &amp; confirm
            </button>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-medium text-red-700 bg-red-50 rounded-lg px-3 py-2">
                This will delete all schedule items from <strong>{purgeStart}</strong> to <strong>{purgeEnd}</strong>. Are you sure?
              </p>
              <div className="flex gap-2">
                <button
                  className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 transition-colors"
                  onClick={() => setPurgeConfirmed(false)}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-40"
                  disabled={purge.isPending}
                  onClick={() => purge.mutate()}
                >
                  {purge.isPending ? "Purging…" : "Yes, purge"}
                </button>
              </div>
            </div>
          )}

          {purgeResult && (
            <div className="mt-4 rounded-lg bg-gray-50 px-4 py-3 text-xs">
              <p className="text-red-700 font-medium">Purge complete</p>
              <p className="text-gray-600">Deleted: <strong>{purgeResult.deleted}</strong></p>
            </div>
          )}

          {purge.isError && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">
              {(purge.error as Error)?.message ?? "An error occurred"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Week Toggle ──────────────────────────────────────────────────────────────

function WeekToggle({ value, onChange }: { value: Week; onChange: (w: Week) => void }) {
  return (
    <div className="flex rounded-lg border border-gray-200 overflow-hidden">
      {([
        { key: "this_week" as Week, label: "This week" },
        { key: "next_week" as Week, label: "Next week" },
      ]).map(({ key, label }) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${
            value === key
              ? "bg-gray-800 text-white"
              : "bg-white text-gray-500 hover:bg-gray-50"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// ─── Result Card ──────────────────────────────────────────────────────────────

function ResultCard({ result, label }: { result: ToolResult; label: string }) {
  const [showErrors, setShowErrors] = useState(false);

  return (
    <>
      <div className="mt-4 rounded-lg bg-gray-50 px-4 py-3 text-xs space-y-1">
        <p className="text-green-700 font-medium">{label}</p>
        <p className="text-gray-600">Created: <strong>{result.created}</strong></p>
        <p className="text-gray-600">Skipped: <strong>{result.skipped}</strong></p>
        {result.errors.length > 0 && (
          <button
            onClick={() => setShowErrors(true)}
            className="text-red-500 hover:text-red-700 underline transition-colors"
          >
            Errors: {result.errors.length}
          </button>
        )}
      </div>

      {showErrors && (
        <Modal title={`Errors (${result.errors.length})`} onClose={() => setShowErrors(false)}>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {result.errors.map((err, i) => (
              <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 text-xs text-gray-700 font-mono whitespace-pre-wrap break-all">
                {JSON.stringify(err, null, 2)}
              </div>
            ))}
          </div>
        </Modal>
      )}
    </>
  );
}